import {
  configuredString,
  type KioskPrinterConfig,
  resolveKioskPrinterConfig,
} from '../config/kioskRuntimeConfig';
import type {
  EntrPhoto,
  EntrPrintTransport,
  PrintPayment,
  PrintSelectionInput,
  PrintSelectionResult,
  SelectionItem,
} from '../types';
import {
  type CopyPayment,
  createReliablePrintQueue,
  FREE_COPY,
  noPrintResult,
  type PrintJobItem,
  type ReliablePrintQueueDeps,
  type ReliablePrintQueueOptions,
  toPrintQueueJob,
} from './printQueue';

export interface KioskPrintingRuntimeConfig {
  kioskSlug?: string | null;
  printer: KioskPrinterConfig | null;
}

export interface KioskPrintingRuntime extends EntrPrintTransport {
  initialize(config: KioskPrintingRuntimeConfig): void;
}

export type KioskPrintingRuntimeDeps = Omit<
  ReliablePrintQueueDeps,
  'buildJobs'
>;

const DEFAULT_PRINTING_CONFIG: KioskPrintingRuntimeConfig = {
  kioskSlug: null,
  printer: null,
};

export function createKioskPrintingRuntime(
  deps: KioskPrintingRuntimeDeps,
  options: ReliablePrintQueueOptions = {}
): KioskPrintingRuntime {
  let config = DEFAULT_PRINTING_CONFIG;
  let printInFlight: Promise<PrintSelectionResult> | null = null;
  const queue = createReliablePrintQueue(
    {
      ...deps,
      buildJobs(input, batchKey) {
        if (!config.printer) {
          deps.observer?.recordEvent(
            'kiosk.printing',
            'missing printer config'
          );
          return null;
        }
        const payments = copyPayments(input.payment ?? null);
        return createKioskPrintJobs(
          input.sessionId,
          input.items,
          config.printer,
          config.kioskSlug,
          batchKey
        ).map((job, index) =>
          toPrintQueueJob(
            job,
            input.sessionId,
            input.wireSessionId,
            payments[index] ?? FREE_COPY
          )
        );
      },
    },
    options
  );

  return {
    initialize(nextConfig) {
      config = {
        kioskSlug: configuredString(nextConfig.kioskSlug),
        printer: nextConfig.printer,
      };
      deps.observer?.recordEvent(
        'kiosk.printing',
        'printing runtime initialized',
        {
          kiosk_slug: config.kioskSlug ?? null,
          printer_vertical: config.printer?.verticalPrinterId ?? null,
          printer_horizontal: config.printer?.horizontalPrinterId ?? null,
        }
      );
    },

    async printSelection(
      input: PrintSelectionInput
    ): Promise<PrintSelectionResult> {
      if (printInFlight) {
        deps.observer?.recordEvent(
          'kiosk.printing',
          'print selection skipped because printing is already in progress'
        );
        return noPrintResult('printing already in progress');
      }

      printInFlight = queue.printSelection(input).finally(() => {
        printInFlight = null;
      });
      return printInFlight;
    },
  };
}

export function createSinglePrinterConfig(
  printerId: string | null | undefined
): KioskPrinterConfig | null {
  return resolveKioskPrinterConfig({ printerId });
}

export function chooseKioskPrinter(
  photo: EntrPhoto,
  config: KioskPrinterConfig
): string {
  return photo.aspectRatio > 1
    ? config.horizontalPrinterId
    : config.verticalPrinterId;
}

export function createKioskPrintIdempotencyKey(
  sessionId: string,
  imageKey: string,
  copyIndex: number,
  kioskSlug?: string | null,
  batchKey?: string | null
): string {
  const parts = ['print'];
  if (kioskSlug) parts.push(kioskSlug);
  if (batchKey) parts.push(batchKey);
  parts.push(sessionId, imageKey, String(copyIndex));
  return parts.join('_');
}

export function createKioskPrintJobs(
  sessionId: string,
  items: SelectionItem[],
  config: KioskPrinterConfig,
  kioskSlug?: string | null,
  batchKey?: string | null
): PrintJobItem[] {
  return items.flatMap((item) =>
    Array.from({ length: item.quantity }, (_, copyIndex) => ({
      photo: item.photo,
      quantity: 1,
      printerId: chooseKioskPrinter(item.photo, config),
      copyIndex,
      idempotencyKey: createKioskPrintIdempotencyKey(
        sessionId,
        printAssetKey(item.photo),
        copyIndex,
        kioskSlug,
        batchKey
      ),
    }))
  );
}

// A copy without a price prints as FREE_COPY, without the payment intent.
function isPaidCopy(amountCents: number): boolean {
  return amountCents > 0;
}

/**
 * The amounts of the copies that carry the card payment, in print order: the
 * declaration of the order at the create of the payment intent. The print
 * submits exactly these copies with the payment intent (see copyPayments),
 * thus the backend knows when the last copy of the order has its result.
 *
 * @param copyAmountsCents - the amount due of each copy, from the quote
 *   (PaymentQuote.copyAmountsCents)
 */
export function paidCopyAmounts(copyAmountsCents: readonly number[]): number[] {
  return copyAmountsCents.filter(isPaidCopy);
}

// The quote gives one amount for each copy of the items, in the order of
// createKioskPrintJobs. A copy without an amount prints as FREE_COPY.
function copyPayments(payment: PrintPayment | null): CopyPayment[] {
  if (!payment) return [];
  return payment.copyAmountsCents.map(
    (amountCents): CopyPayment =>
      isPaidCopy(amountCents)
        ? {
            amountCents,
            paymentIntentId: payment.paymentIntentId,
            paymentSessionId: payment.paymentSessionId,
          }
        : FREE_COPY
  );
}

function printAssetKey(photo: EntrPhoto): string {
  return photo.printImageUuid ?? photo.id;
}
